import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { MINIMUM_ACCEPTED_AUDIO_DURATION } from '@modals/PriceModal/constants';
import RefreshBalance from '@components/icons/RefreshBalance.svg';
import { convertDuration } from '@helpers/convertDuration';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { useAuthContext } from '@providers/AuthProvider';
import type { TProps } from './types/TProps';

export function PriceModalPriceDescription({
  price,
  setDataLoaded,
  balanceIsRefreshing,
  setBalanceIsRefreshing,
  audioDuration,
  enhancedPaymentVariant,
  freeTrialInEffect,
}: TProps) {
  const { updateMe, organization } = useAuthContext();
  const initialRate = enhancedPaymentVariant
    ? organization?.billingAccount?.billingConfiguration?.enhancedOnDemandRate ?? 0
    : price?.ASRRate ?? 0;
  const initialRateNumber = Number(initialRate);

  const balance = organization?.billingAccount?.currentBalance ?? 0;

  const priceText = freeTrialInEffect ? (
    <div>
      <span className="text-[var(--color-text-muted)] mr-4 font-normal">Free trial period</span>{' '}
      <span className="line-through opacity-40 mr-4">
        {formatCentsPrice(initialRateNumber * 100 * ((price?.duration ?? 0) / 3600))}
      </span>{' '}
      <span>$0</span>
    </div>
  ) : (
    <span>{formatCentsPrice(price?.price)}</span>
  );

  const audioHourPrice = freeTrialInEffect ? (
    <div>
      <span className="line-through opacity-40 mr-4">${initialRateNumber.toFixed(2)}</span> <span>$0</span>
    </div>
  ) : (
    <span>${initialRateNumber.toFixed(2)}</span>
  );

  return (
    <div>
      <div className="title">Current balance</div>
      <div className={`title-value ${balanceIsRefreshing ? 'balance-refresh' : ''}`}>
        {formatCentsPrice(balance)}
        <RefreshBalance
          onClick={() => {
            if (balanceIsRefreshing) {
              return;
            }
            setBalanceIsRefreshing(true);

            updateMe()
              .then(() => {
                setBalanceIsRefreshing(false);
                setDataLoaded(true);
              })
              .catch(() => {
                setBalanceIsRefreshing(false);
                setDataLoaded(true);
              });
          }}
        />
      </div>

      <p className="description-text">
        <span className="block text-wrap text-left">
          {enhancedPaymentVariant
            ? 'After applying AI Enhancements to this job you will be charged:'
            : 'After submitting this job you will be charged:'}
        </span>
      </p>
      <div className="text-end">{priceText}</div>

      <div className="dflex">
        <span>Duration:</span>
        {convertDuration(price?.duration ?? 0)}
      </div>

      <div className="dflex">
        <span>Cost per audio hour:</span> {audioHourPrice}
      </div>

      {enhancedPaymentVariant && <ErrorMessage error={{ code: 'wrn.enhanced' }} />}

      {audioDuration < MINIMUM_ACCEPTED_AUDIO_DURATION && (
        <ErrorMessage testid="durationError" error={{ code: 'err.duration' }} />
      )}
    </div>
  );
}
